import { useMutation } from "@apollo/client";
import { useCallback } from "react";

import markReportOnboardedMutation from "@apps/profile/graphql/mark-report-onboarded-mutation";
import { currentUserVar } from "@cache/cache";
import { useLink } from "@components/link/link-helpers";

// Marks a direct report onboarded (optimistic cache update + persisted mutation) and, for
// `startReportOnboarding`, launches the /onboard-direct-report skill in Ask AI. Shared by the
// profile onboarding gate, the homepage/profile onboarding CTA, and the meeting prep card.
export const useStartReportOnboarding = () => {
  const [markReportOnboarded] = useMutation(markReportOnboardedMutation);
  const link = useLink();

  const persistReportOnboarded = useCallback(
    (reportId: number) => {
      const current = currentUserVar();
      let data: { onboarded_report_ids?: number[] } = {};
      try {
        data = current.onboardingData ? JSON.parse(current.onboardingData) : {};
      } catch {
        data = {};
      }
      const ids = Array.isArray(data.onboarded_report_ids)
        ? data.onboarded_report_ids
        : [];
      if (!ids.includes(reportId)) {
        currentUserVar({
          ...current,
          onboardingData: JSON.stringify({
            ...data,
            onboarded_report_ids: [...ids, reportId],
          }),
        });
      }
      markReportOnboarded({ variables: { reportId } });
    },
    [markReportOnboarded],
  );

  const startReportOnboarding = useCallback(
    (report: { id: number; name: string }) => {
      persistReportOnboarded(report.id);
      const prompt = `/onboard-direct-report ${report.name}`.trim();
      // autosubmit=1 sends the skill prompt immediately so the chat starts without the manager pressing send.
      link.redirect(`/ask-ai?p=${encodeURIComponent(prompt)}&autosubmit=1`);
    },
    [persistReportOnboarded, link],
  );

  return { startReportOnboarding, persistReportOnboarded };
};

export default useStartReportOnboarding;
